import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, signal, untracked } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  DeleteImpact,
  SanctionedPlan,
  SbcApiService,
  SystemLocation,
} from '../services/sbc-api.service';
import { FILES_BASE } from '../services/api.config';
import { SessionService } from '../../../core/services/session.service';
import { StatusBadgeComponent } from '../components/status-badge.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';

export interface PlanVerification {
  verdict?: string;
  status?: string;
  matches?: boolean;
  compared?: Array<{
    field: string;
    planValue: unknown;
    measuredValue: unknown;
    tolerance?: number;
    status?: string;
    iou?: number;
  }>;
  differences?: Array<{
    ruleName?: string;
    detail?: string;
    planValue?: unknown;
    measuredValue?: unknown;
  }>;
}

export interface PlotMatchCheck {
  verdict: 'match' | 'mismatch' | 'unknown' | 'warning';
  headline: string;
  detail: string;
  pdfPlotRef: string | null;
  checks: Array<{ label: string; pdf: string; master: string; ok: boolean | null }>;
}

@Component({
  selector: 'app-plans',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    StatusBadgeComponent,
    ConfirmDialogComponent,
    PageBackButtonComponent,
    PageRefreshButtonComponent,
  ],
  templateUrl: './plans.component.html',
  styleUrl: './plans.component.css',
})
export class PlansComponent implements OnDestroy {
  private readonly api = inject(SbcApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly sanitizer = inject(DomSanitizer);
  readonly session = inject(SessionService);

  rows = signal<SanctionedPlan[]>([]);
  selectedPlanId = signal<number | null>(null);
  error = signal('');
  toast = signal('');
  loading = signal(false);
  processing = signal(false);
  activeTab = signal<'browse' | 'upload'>('browse');
  wizardStep = signal(1);
  dragOver = signal(false);
  fileWarn = signal('');
  uploadFormErrors = signal<{ file?: string; plotRef?: string; form?: string }>({});
  deleteConfirmOpen = signal(false);
  deleteConfirmMessage = signal('');
  deleteImpactLoadingId = signal<number | null>(null);
  deletingPlanId = signal<number | null>(null);
  private pendingDeletePlan = signal<SanctionedPlan | null>(null);
  private digitizePollTimer: ReturnType<typeof setInterval> | null = null;
  lastOcr = signal<{
    planId: number;
    plotRef: string;
    engine?: string;
    ocrConfidence?: string | number | null;
    note?: string;
    sourceBytes?: number | null;
    dimensions?: Record<string, unknown> | null;
    ocrText?: string;
    cleanedUri?: string | null;
    originalUri?: string | null;
    planVerification?: PlanVerification | null;
  } | null>(null);

  localPreviewUrl = signal<string | null>(null);
  previewMode = signal<'auto' | 'pdf'>('auto');

  form = {
    pilotAreaId: '1',
    plotRef: '',
    planNo: '',
    originalStorageUri: '',
    sourceType: 'sample_public_pdf',
    qualityClass: 'hand_drawn',
  };

  sampleUrl = '';
  uploadFile: File | null = null;
  lidarFile: File | null = null;
  comparingLidar = signal(false);
  plotOptions = signal<Array<{ plotRef: string; label: string }>>([]);
  plotLocationsByRef = signal<Map<string, SystemLocation>>(new Map());
  plotsLoading = signal(false);

  /** One row per plot — latest / best-status plan only (no duplicate plot refs). */
  displayRows = computed(() => {
    const byPlot = new Map<string, SanctionedPlan>();
    for (const plan of this.rows()) {
      const ref = String(plan.plotRef || '').trim();
      if (!ref) continue;
      const existing = byPlot.get(ref);
      if (!existing || this.isPlanPreferred(plan, existing)) {
        byPlot.set(ref, plan);
      }
    }
    return [...byPlot.values()].sort((a, b) => {
      const ta = a.txnStart ? new Date(a.txnStart).getTime() : 0;
      const tb = b.txnStart ? new Date(b.txnStart).getTime() : 0;
      return tb - ta;
    });
  });

  /** Plots with no active plan yet — for upload dropdown. */
  availablePlotOptions = computed(() =>
    this.plotOptions().filter((p) => !this.plotHasActivePlan(p.plotRef))
  );

  plotCount = computed(() => this.displayRows().length);
  canDeletePlans = computed(() =>
    ['config_admin', 'verifier'].includes(this.session.role())
  );

  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => {
        this.reload();
        this.loadPlotOptions();
      });
    });

    this.route.queryParamMap.subscribe((params) => {
      const planId = Number(params.get('planId'));
      if (planId > 0) {
        const plan = this.rows().find((p) => p.id === planId);
        if (plan) this.openPlan(plan);
      }
    });
  }

  selectedPlan(): SanctionedPlan | null {
    const id = this.selectedPlanId();
    if (id == null) return null;
    return this.rows().find((p) => p.id === id) || null;
  }

  isPlanSelected(plan: SanctionedPlan): boolean {
    return this.selectedPlanId() === plan.id;
  }

  planStatusLabel(statusCd?: string): string {
    const map: Record<string, string> = {
      verified: 'Verified',
      pending_verify: 'Pending verify',
      uploaded: 'Uploaded',
      rejected: 'Rejected',
    };
    return map[statusCd || ''] || statusCd || '—';
  }

  uploadDateLabel(plan: SanctionedPlan): string {
    const raw = plan.txnStart;
    if (!raw) return '—';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? raw : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }

  openPlanById(planId: number): void {
    const plan = this.rows().find((p) => p.id === planId);
    if (plan) this.openPlan(plan);
  }

  setTab(tab: 'browse' | 'upload'): void {
    this.activeTab.set(tab);
    if (tab === 'upload') {
      this.wizardStep.set(1);
    }
  }

  clearSelectedPlan(): void {
    this.selectedPlanId.set(null);
    this.lastOcr.set(null);
    this.wizardStep.set(1);
  }

  requestDeletePlan(plan: SanctionedPlan, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDeletePlans() || this.deleteImpactLoadingId() != null) return;
    this.deleteImpactLoadingId.set(plan.id);
    this.api.getPlanDeleteImpact(plan.id).subscribe({
      next: (res) => {
        this.deleteImpactLoadingId.set(null);
        this.pendingDeletePlan.set(plan);
        this.deleteConfirmMessage.set(this.planDeleteMessage(plan, res.data));
        this.deleteConfirmOpen.set(true);
      },
      error: (err) => {
        this.deleteImpactLoadingId.set(null);
        this.showToast(err?.error?.message || 'Unable to check plan dependencies');
      },
    });
  }

  confirmDeletePlan(): void {
    const plan = this.pendingDeletePlan();
    if (!plan || this.deletingPlanId() != null) return;
    this.deleteConfirmOpen.set(false);
    this.deletingPlanId.set(plan.id);
    this.api.deletePlan(plan.id).subscribe({
      next: (res) => {
        this.deletingPlanId.set(null);
        this.pendingDeletePlan.set(null);
        if (this.selectedPlanId() === plan.id) this.clearSelectedPlan();
        this.showToast((res.meta?.['message'] as string) || 'Sanctioned plan removed');
        this.reload();
      },
      error: (err) => {
        this.deletingPlanId.set(null);
        this.showToast(err?.error?.message || 'Failed to remove sanctioned plan');
      },
    });
  }

  cancelDeletePlan(): void {
    this.deleteConfirmOpen.set(false);
    this.pendingDeletePlan.set(null);
  }

  private planDeleteMessage(plan: SanctionedPlan, impact: DeleteImpact): string {
    const name = plan.planNo || `plan #${plan.id}`;
    if (!impact.hasDerivedData) {
      return `Delete ${name} for Khasra ${plan.plotRef}? This action cannot be undone.`;
    }
    return `Delete ${name} for Khasra ${plan.plotRef}? Warning: this plan is already verified/processed or has related data (${impact.measurementCount} measurement(s), ${impact.flagCount} flag(s)). Related data will be retained for audit, but the PDF will no longer be available.`;
  }

  reload(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listPlans().subscribe({
      next: (res) => {
        const sorted = [...(res.data || [])].sort((a, b) => {
          const ta = a.txnStart ? new Date(a.txnStart).getTime() : 0;
          const tb = b.txnStart ? new Date(b.txnStart).getTime() : 0;
          return tb - ta;
        });
        this.rows.set(sorted);
        this.loading.set(false);
        const planId = Number(this.route.snapshot.queryParamMap.get('planId'));
        if (planId > 0) {
          const plan = this.rows().find((p) => p.id === planId);
          if (plan) this.openPlan(plan);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load plans');
      },
    });
  }

  loadPlotOptions(): void {
    this.plotsLoading.set(true);
    this.api.listSystemLocations().subscribe({
      next: (res) => {
        const options = (res.data || [])
          .filter((loc) => loc.locationTypeCode === 'PLOT' && loc.statusCd === 'active')
          .map((loc) => ({
            plotRef: this.plotRefFromLocation(loc),
            label: this.plotOptionLabel(loc),
          }))
          .filter((o) => o.plotRef)
          .sort((a, b) => a.label.localeCompare(b.label));
        this.plotOptions.set(options);
        const byRef = new Map<string, SystemLocation>();
        for (const loc of res.data || []) {
          if (loc.locationTypeCode !== 'PLOT' || loc.statusCd !== 'active') continue;
          const ref = this.plotRefFromLocation(loc);
          if (ref) byRef.set(ref, loc);
        }
        this.plotLocationsByRef.set(byRef);
        this.plotsLoading.set(false);
      },
      error: () => {
        this.plotOptions.set([]);
        this.plotLocationsByRef.set(new Map());
        this.plotsLoading.set(false);
      },
    });
  }

  plotRefFromLocation(loc: SystemLocation): string {
    const attrs = loc.attributes || {};
    const fromAttrs =
      attrs['plot_ref'] ||
      attrs['plotRef'] ||
      attrs['khasra_number'] ||
      attrs['khasra_no'] ||
      attrs['khasraNumber'];
    if (fromAttrs != null && String(fromAttrs).trim()) return String(fromAttrs).trim();
    return String(loc.code || '').trim();
  }

  private plotOptionLabel(loc: SystemLocation): string {
    const plotRef = this.plotRefFromLocation(loc);
    return `${loc.name} · ${plotRef}`;
  }

  plotForRef(plotRef: string | null | undefined): SystemLocation | null {
    const ref = String(plotRef || '').trim();
    if (!ref) return null;
    return this.plotLocationsByRef().get(ref) ?? null;
  }

  plotLocationPath(loc: SystemLocation): string {
    const parts: string[] = [];
    if (loc.parentName) parts.push(loc.parentName);
    parts.push(loc.name);
    return parts.join(' · ');
  }

  plotDetailRows(loc: SystemLocation): Array<{ label: string; value: string }> {
    const rows: Array<{ label: string; value: string }> = [];
    for (const field of loc.fields || []) {
      if (field.key === 'khasra_number') continue;
      const raw = loc.attributes?.[field.key];
      if (raw == null || String(raw).trim() === '') continue;
      rows.push({ label: field.label, value: String(raw) });
    }
    return rows;
  }

  plotAttr(loc: SystemLocation, key: string): string {
    const raw = loc.attributes?.[key];
    if (raw == null || String(raw).trim() === '') return '—';
    return String(raw);
  }

  plotSummaryLine(loc: SystemLocation): string {
    const parts = [
      loc.parentName,
      `${this.plotAttr(loc, 'plot_area_sqm')} sq.m`,
      this.plotAttr(loc, 'land_use_type'),
      this.plotHasBoundary(loc) ? 'Boundary on map' : 'No boundary yet',
    ].filter((p) => p && p !== '—');
    return parts.join(' · ');
  }

  plotHasActivePlan(plotRef: string | null | undefined): boolean {
    const ref = String(plotRef || '').trim();
    if (!ref) return false;
    return this.rows().some(
      (p) => String(p.plotRef || '').trim() === ref && p.statusCd !== 'rejected'
    );
  }

  private static planStatusRank(status?: string): number {
    const map: Record<string, number> = {
      verified: 4,
      pending_verify: 3,
      uploaded: 2,
      rejected: 1,
    };
    return map[status || ''] ?? 0;
  }

  private isPlanPreferred(a: SanctionedPlan, b: SanctionedPlan): boolean {
    const rankDiff =
      PlansComponent.planStatusRank(a.statusCd) - PlansComponent.planStatusRank(b.statusCd);
    if (rankDiff !== 0) return rankDiff > 0;
    const ta = a.txnStart ? new Date(a.txnStart).getTime() : 0;
    const tb = b.txnStart ? new Date(b.txnStart).getTime() : 0;
    return ta > tb;
  }

  plotHasBoundary(loc: SystemLocation): boolean {
    const geo = loc.boundaryGeojson;
    return !!(geo && typeof geo === 'object' && Object.keys(geo).length > 0);
  }

  pdfPlotRefFromOcr(ocr: { dimensions?: Record<string, unknown> | null; ocrText?: string }): string | null {
    const dims = ocr.dimensions;
    const stored = dims?.['pdfPlotRef'];
    if (stored != null && String(stored).trim()) return String(stored).trim();
    const text =
      String(ocr.ocrText || '') ||
      String(dims?.['ocrText'] || '') ||
      String(dims?.['ocrTextPreview'] || '');
    return this.extractPlotRefFromText(text);
  }

  private extractPlotRefFromText(text: string): string | null {
    if (!text?.trim()) return null;
    const patterns = [
      /\bPLOT\s*REF\s*[:.]?\s*([A-Za-z0-9\-]+)/i,
      /\bKHASRA\s*(?:NO\.?|NUMBER)?\s*[:.]?\s*(\d+)/i,
      /\bKhasra\s*[-–]?\s*(\d+)/i,
      /\bPlot\s*[-–]?\s*(\d+)/i,
    ];
    for (const re of patterns) {
      const m = text.match(re);
      if (m?.[1]) return String(m[1]).trim();
    }
    return null;
  }

  private normalizePlotToken(value: string): string {
    const s = String(value || '').trim();
    const trailing = s.match(/(\d+)\s*$/);
    return trailing ? trailing[1] : s.toUpperCase();
  }

  private plotRefsMatch(selected: string, pdfRef: string): boolean {
    if (selected === pdfRef) return true;
    return this.normalizePlotToken(selected) === this.normalizePlotToken(pdfRef);
  }

  assessPlotMatch(
    plotRef: string | null | undefined,
    ocr: { dimensions?: Record<string, unknown> | null; ocrText?: string },
    plot: SystemLocation | null
  ): PlotMatchCheck {
    const selected = String(plotRef || '').trim();
    const pdfRef = this.pdfPlotRefFromOcr(ocr);
    const checks: PlotMatchCheck['checks'] = [];

    let verdict: PlotMatchCheck['verdict'] = 'unknown';
    let headline = 'Plot number not found clearly in PDF';
    let detail =
      'Open the plan PDF — the Khasra / plot ref inside should match the plot you selected.';

    if (pdfRef && selected) {
      const refOk = this.plotRefsMatch(selected, pdfRef);
      checks.push({
        label: 'Khasra / Plot ref',
        pdf: pdfRef,
        master: `Khasra ${selected}`,
        ok: refOk,
      });
      if (refOk) {
        verdict = 'match';
        headline = 'Yes — this plan appears to be for the selected plot';
        detail = `The PDF shows ${pdfRef} and you selected Khasra ${selected}. They match.`;
      } else {
        verdict = 'mismatch';
        headline = 'No — this plan appears to be for a different plot';
        detail = `The PDF shows ${pdfRef}, but you selected Khasra ${selected}. Choose the correct plot and upload again.`;
      }
    } else if (pdfRef) {
      checks.push({
        label: 'Khasra / Plot ref (PDF)',
        pdf: pdfRef,
        master: '—',
        ok: null,
      });
    }

    const pdfArea = ocr.dimensions?.['plotAreaSqm'];
    const masterAreaRaw = plot ? plot.attributes?.['plot_area_sqm'] : null;
    if (pdfArea != null && masterAreaRaw != null && String(masterAreaRaw).trim()) {
      const pdfN = Number(pdfArea);
      const masterN = Number(masterAreaRaw);
      if (!Number.isNaN(pdfN) && !Number.isNaN(masterN)) {
        const diff = Math.abs(pdfN - masterN);
        const areaOk = diff <= Math.max(5, masterN * 0.08);
        checks.push({
          label: 'Plot area',
          pdf: `${pdfN} sq.m`,
          master: `${masterN} sq.m`,
          ok: areaOk,
        });
        if (verdict === 'match' && !areaOk) {
          verdict = 'warning';
          headline = 'Plot ref matches, but area differs';
          detail = `PDF area is ${pdfN} sq.m; master data shows ${masterN} sq.m — review manually before verifying.`;
        }
      }
    }

    return { verdict, headline, detail, pdfPlotRef: pdfRef, checks };
  }

  plotMatchVerdictClass(verdict: PlotMatchCheck['verdict']): string {
    return `plot-match-verdict--${verdict}`;
  }

  create(): void {
    this.api
      .createPlan({
        pilotAreaId: Number(this.form.pilotAreaId) || null,
        plotRef: this.form.plotRef,
        planNo: this.form.planNo,
        originalStorageUri: this.form.originalStorageUri,
        sourceType: this.form.sourceType,
        qualityClass: this.form.qualityClass,
      })
      .subscribe({
        next: () => {
          this.showToast('Plan registered');
          this.form.planNo = '';
          this.reload();
        },
        error: (err) => this.showToast(err?.error?.message || 'Create failed (config_admin / verifier)'),
      });
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.uploadFile = input.files?.[0] || null;
    this.updateFileWarn();
    this.refreshLocalPreview();
    if (this.uploadFile) {
      this.uploadFormErrors.update((e) => ({ ...e, file: undefined, form: undefined }));
    }
  }

  private updateFileWarn(): void {
    const name = (this.uploadFile?.name || '').toLowerCase();
    if (name.includes('demo-01') || name.includes('bye-law')) {
      this.fileWarn.set('Bye-law plan PDF — extracts height, setback, FAR, coverage, and plot area.');
    } else if (name.includes('demo-02') || name.includes('floor-plan')) {
      this.fileWarn.set('Floor plan PDF — drawing sheet with dimension labels.');
    } else if (name.includes('01-') || name.includes('sanction')) {
      this.fileWarn.set('Sanction letter — text + Gemini OCR + footprint polygon.');
    } else if (name.includes('02-') || name.includes('03-') || name.includes('floor-plan') || name.includes('building-plan')) {
      this.fileWarn.set('Drawing PDF — Gemini OCR will extract dimensions and building footprint polygon.');
    } else {
      this.fileWarn.set('');
    }
  }

  previewUrl(): string | null {
    const preview = this.documentPreview();
    return preview?.kind === 'image' ? preview.src : null;
  }

  documentPreview(): { kind: 'image' | 'pdf'; src: string } | null {
    const ocr = this.lastOcr();
    const bust = ocr?.planId ? `?v=${ocr.planId}` : '';
    const mode = this.previewMode();

    // Prefer high-res page PNG from digitize (PyMuPDF 2× render) — clearer than embedded PDF viewer zoom.
    if (mode !== 'pdf' && ocr?.cleanedUri && this.isImagePath(ocr.cleanedUri)) {
      const src = this.fileUrl(ocr.cleanedUri);
      if (src) return { kind: 'image', src: `${src}${bust}` };
    }

    const pdfUri = ocr?.originalUri || this.pdfUriFromCleaned(ocr?.cleanedUri);
    if (pdfUri) {
      const src = this.fileUrl(pdfUri);
      if (src) return { kind: 'pdf', src: this.pdfEmbedUrl(`${src}${bust}`) };
    }

    const local = this.localPreviewUrl();
    if (local) return { kind: 'pdf', src: local };

    return null;
  }

  safePreviewUrl(url: string | null | undefined): SafeResourceUrl | null {
    if (!url) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  previewOpenUrl(): string | null {
    const preview = this.documentPreview();
    if (preview?.kind === 'pdf') return preview.src;
    const ocr = this.lastOcr();
    const pdfUri = ocr?.originalUri || this.pdfUriFromCleaned(ocr?.cleanedUri);
    if (!pdfUri) return null;
    const src = this.fileUrl(pdfUri);
    return src ? this.pdfEmbedUrl(src) : null;
  }

  private pdfEmbedUrl(url: string): string {
    if (!url || url.startsWith('blob:')) return url;
    if (url.includes('#')) return url;
    return `${url}#view=FitH&zoom=page-width`;
  }

  onPreviewImageError(): void {
    this.previewMode.set('pdf');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(true);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))) {
      this.uploadFile = file;
      this.updateFileWarn();
      this.refreshLocalPreview();
      this.uploadFormErrors.update((e) => ({ ...e, file: undefined, form: undefined }));
    } else {
      this.uploadFormErrors.set({
        file: 'Please drop a PDF file only.',
        form: 'Fix the highlighted fields to continue.',
      });
    }
  }

  onPlotRefChange(): void {
    if (this.form.plotRef.trim()) {
      this.uploadFormErrors.update((e) => ({ ...e, plotRef: undefined, form: undefined }));
    }
  }

  goToUpload(): void {
    this.activeTab.set('upload');
    this.wizardStep.set(1);
    this.uploadFile = null;
    this.fileWarn.set('');
    this.uploadFormErrors.set({});
    this.previewMode.set('auto');
    this.revokeLocalPreview();
  }

  openPlan(plan: SanctionedPlan): void {
    this.activeTab.set('browse');
    this.selectedPlanId.set(plan.id);
    this.viewExtracted(plan);
    const hasFields = this.extractedRows({ dimensions: plan.dimensionsJson }).length > 0;
    this.wizardStep.set(hasFields ? (plan.statusCd === 'pending_verify' ? 3 : 2) : 2);
  }

  private afterExtract(planId: number, hasFields: boolean): void {
    const row = this.rows().find((r) => r.id === planId);
    if (row?.statusCd === 'verified') {
      this.wizardStep.set(3);
    } else if (hasFields) {
      this.wizardStep.set(row?.statusCd === 'pending_verify' ? 3 : 2);
    } else {
      this.wizardStep.set(2);
    }
  }

        uploadPdf(): void {
    const file = this.uploadFile;
    const plotRef = this.form.plotRef.trim();
    if (!file || !plotRef) {
      const errors: { file?: string; plotRef?: string; form?: string } = {};
      if (!file) {
        errors.file = 'Choose a PDF file first.';
      }
      if (!plotRef) {
        errors.plotRef = 'Select the plot this plan belongs to.';
      }
      errors.form = 'Fix the highlighted fields to continue.';
      this.uploadFormErrors.set(errors);
      return;
    }
    if (this.plotHasActivePlan(plotRef)) {
      this.uploadFormErrors.set({
        plotRef: 'This plot already has a plan — open it from the list below.',
        form: 'Fix the highlighted fields to continue.',
      });
      return;
    }

    this.uploadFormErrors.set({});
    this.processing.set(true);
    this.showToast(this.uploadProgressMessage(file));
    const fd = new FormData();
    fd.append('file', file);
    fd.append('plotRef', plotRef);
    fd.append('planNo', this.form.planNo || '');
    fd.append('pilotAreaId', String(this.form.pilotAreaId || '1'));
    fd.append('qualityClass', this.form.qualityClass);
    this.api.uploadPlan(fd).subscribe({
      next: (res) => {
        const plan = res.data;
        const dig = (res.meta?.['digitize'] as Record<string, unknown>) || {};
        if (dig['processing']) {
          this.selectedPlanId.set(plan.id);
          this.activeTab.set('browse');
          this.uploadFile = null;
          this.revokeLocalPreview();
          this.reload();
          this.pollUntilDigitized(
            plan.id,
            Number(dig['estimatedSeconds'] || 90),
            String(dig['message'] || 'Extracting plan in background…')
          );
          return;
        }
        this.processing.set(false);
        this.setLastOcrFromPlan(plan, {
          engine: dig['engine'] || 'digitize',
          ocrText: dig['ocrText'] || '',
          note: dig['note'] || '',
          planVerification: (dig['planVerification'] as PlanVerification) || null,
        });
        this.selectedPlanId.set(plan.id);
        this.activeTab.set('browse');
        const n = this.extractedRows(this.lastOcr()!).length;
        this.wizardStep.set(n > 0 ? (plan.statusCd === 'pending_verify' ? 3 : 2) : 2);
        this.showToast(
          n > 0
            ? `PDF processed — ${n} fields extracted (see table below)`
            : 'PDF uploaded but extract failed — backend restart + Process PDF again'
        );
        this.uploadFile = null;
        this.revokeLocalPreview();
        this.reload();
      },
      error: (err) => {
        this.processing.set(false);
        this.showToast(err?.error?.message || 'Upload failed');
      },
    });
  }

  private uploadProgressMessage(file: File): string {
    const mb = file.size / (1024 * 1024);
    if (mb < 1.5) {
      return 'Uploading — small PDF, usually ready in a few seconds…';
    }
    if (mb < 4) {
      return 'Extracting plan data — about 30–60 seconds for scanned drawings…';
    }
    return 'Large PDF — saving now; field extraction continues in the background…';
  }

  private pollUntilDigitized(planId: number, estimatedSeconds: number, startMessage: string): void {
    this.clearDigitizePoll();
    const started = Date.now();
    const maxMs = Math.max(estimatedSeconds * 2000, 120_000);
    this.showToast(startMessage);
    this.digitizePollTimer = setInterval(() => {
      if (Date.now() - started > maxMs) {
        this.clearDigitizePoll();
        this.processing.set(false);
        this.showToast('Extraction still running — refresh or tap Re-process PDF in a minute');
        return;
      }
      this.api.listPlans().subscribe({
        next: (res) => {
          const sorted = [...(res.data || [])].sort((a, b) => {
            const ta = a.txnStart ? new Date(a.txnStart).getTime() : 0;
            const tb = b.txnStart ? new Date(b.txnStart).getTime() : 0;
            return tb - ta;
          });
          this.rows.set(sorted);
          const plan = sorted.find((p) => p.id === planId);
          if (!plan) return;
          if (plan.statusCd === 'pending_verify' || plan.statusCd === 'verified') {
            this.clearDigitizePoll();
            this.processing.set(false);
            this.selectedPlanId.set(plan.id);
            this.openPlan(plan);
            const n = this.extractedRows(this.lastOcr()!).length;
            this.showToast(
              n > 0
                ? `PDF ready — ${n} fields extracted`
                : 'PDF processed — open the plan to review extracted fields'
            );
          } else if (plan.statusCd === 'uploaded') {
            const elapsed = Math.round((Date.now() - started) / 1000);
            this.showToast(`Still extracting… (~${elapsed}s / est. ${estimatedSeconds}s)`);
          }
        },
      });
    }, 2500);
  }

  private clearDigitizePoll(): void {
    if (this.digitizePollTimer) {
      clearInterval(this.digitizePollTimer);
      this.digitizePollTimer = null;
    }
  }

  ingestSample(): void {
    this.api
      .ingestSamplePlan({
        pilotAreaId: Number(this.form.pilotAreaId) || 1,
        plotRef: this.form.plotRef || 'PLOT-SAMPLE',
        planNo: this.form.planNo || undefined,
        sourceUrl: this.sampleUrl.trim() || null,
        qualityClass: this.form.qualityClass,
      })
      .subscribe({
        next: (res) => {
          const src = res.meta?.['ingest'] as { source?: string; bytes?: number } | undefined;
          this.showToast(
            `Plan PDF saved (${src?.bytes ?? '?'} bytes) — click Digitize (OCR)`
          );
          this.reload();
        },
        error: (err) => this.showToast(err?.error?.message || 'Plan ingest failed'),
      });
  }

  digitize(id: number): void {
    const row = this.rows().find((r) => r.id === id);
    const estSec =
      row?.dimensionsJson && typeof row.dimensionsJson['pdfPageCount'] === 'number'
        ? 20 + Number(row.dimensionsJson['pdfPageCount']) * 8
        : 45;
    this.processing.set(true);
    this.showToast(
      estSec <= 25
        ? 'Re-processing PDF — usually a few seconds…'
        : `Re-processing PDF — about ${estSec} seconds…`
    );
    this.api.digitizePlan(id).subscribe({
      next: (res) => {
        this.processing.set(false);
        this.setLastOcrFromPlan(res.data, res.meta || {});
        this.selectedPlanId.set(res.data.id);
        this.activeTab.set('browse');
        const n = this.extractedRows(this.lastOcr()!).length;
        this.afterExtract(id, n > 0);
        this.showToast(n > 0 ? `${n} fields extracted` : 'Extract failed — restart backend');
        this.reload();
      },
      error: (err) => {
        this.processing.set(false);
        this.showToast(err?.error?.message || 'Process failed');
      },
    });
  }

  canVerify(planId: number): boolean {
    const row = this.rows().find((r) => r.id === planId);
    if (row?.statusCd !== 'pending_verify') return false;
    const ocr = this.lastOcr();
    if (ocr?.planId === planId) {
      const check = this.assessPlotMatch(row.plotRef, ocr, this.plotForRef(row.plotRef));
      if (check.verdict === 'mismatch') return false;
    }
    return true;
  }

  verifyBlockedReason(planId: number): string | null {
    const row = this.rows().find((r) => r.id === planId);
    const ocr = this.lastOcr();
    if (!row || ocr?.planId !== planId) return null;
    const check = this.assessPlotMatch(row.plotRef, ocr, this.plotForRef(row.plotRef));
    if (check.verdict === 'mismatch') {
      return 'Wrong plot — select the correct plot and upload again before verifying.';
    }
    return null;
  }

  viewExtracted(plan: SanctionedPlan): void {
    const dims = plan.dimensionsJson || null;
    const ocrText =
      dims && typeof dims['ocrText'] === 'string' ? String(dims['ocrText']) : '';
    this.lastOcr.set({
      planId: plan.id,
      plotRef: plan.plotRef,
      engine: String(dims?.['engine'] || 'stored'),
      ocrConfidence: plan.ocrConfidence,
      note: this.extractedRows({ dimensions: dims }).length
        ? ''
        : 'Legacy record — click Process PDF again after restarting the backend.',
      sourceBytes: null,
      dimensions: dims,
      ocrText,
      cleanedUri: plan.cleanedStorageUri,
      originalUri: plan.originalStorageUri ?? null,
      planVerification: this.readPlanVerification(dims),
    });
    this.previewMode.set('auto');
  }

  extractedRows(ocr: {
    dimensions?: Record<string, unknown> | null;
  }): Array<{ field: string; value: string; unit?: string; rule?: string }> {
    const dims = ocr.dimensions;
    if (!dims) return [];
    const rows = dims['extractedFields'];
    if (!Array.isArray(rows)) return [];
    const skipFields = new Set(['Label', 'raw_text', 'ocrText', 'Plot Ref', 'Plot Ref (selected)']);
    return rows
      .filter((r) => r && typeof r === 'object')
      .map((r) => {
        const o = r as Record<string, unknown>;
        return {
          field: String(o['field'] || ''),
          value: String(o['value'] || ''),
          unit: o['unit'] ? String(o['unit']) : '',
          rule: o['rule'] ? String(o['rule']) : '',
        };
      })
      .filter((r) => r.field && r.value && !skipFields.has(r.field))
      .filter((r) => r.value.length <= 500);
  }

  ocrPreview(ocr: { dimensions?: Record<string, unknown> | null }): string {
    const dims = ocr.dimensions;
    if (!dims) return '';
    const preview = dims['ocrTextPreview'];
    if (typeof preview === 'string' && preview.trim()) return preview.trim();
    const legacy = dims['ocrText'];
    if (typeof legacy === 'string' && legacy.trim()) {
      const t = legacy.replace(/\s+/g, ' ').trim();
      return t.length > 280 ? t.slice(0, 279) + '…' : t;
    }
    return '';
  }

  ocrTextLength(ocr: { dimensions?: Record<string, unknown> | null }): number {
    const dims = ocr.dimensions;
    if (!dims) return 0;
    const n = dims['ocrTextLength'];
    if (typeof n === 'number') return n;
    const legacy = dims['ocrText'];
    if (typeof legacy === 'string') return legacy.length;
    return 0;
  }

  fileUrl(uri?: string | null): string | null {
    if (!uri) return null;
    let rel = String(uri).replace(/\\/g, '/');
    const dataIdx = rel.toLowerCase().indexOf('/data/');
    if (dataIdx >= 0) {
      rel = rel.slice(dataIdx + 1);
    }
    rel = rel.replace(/^data\//, '');
    if (!rel) return null;
    return `${FILES_BASE}/${rel}`;
  }

  private isImagePath(uri: string): boolean {
    return /\.(png|jpe?g|webp|gif)$/i.test(uri);
  }

  private pdfUriFromCleaned(cleanedUri?: string | null): string | null {
    if (!cleanedUri) return null;
    const guess = cleanedUri.replace(/\.cleaned\.png$/i, '');
    return /\.pdf$/i.test(guess) ? guess : null;
  }

  private refreshLocalPreview(): void {
    this.revokeLocalPreview();
    if (this.uploadFile) {
      this.localPreviewUrl.set(URL.createObjectURL(this.uploadFile));
      this.previewMode.set('auto');
    }
  }

  private revokeLocalPreview(): void {
    const current = this.localPreviewUrl();
    if (current) URL.revokeObjectURL(current);
    this.localPreviewUrl.set(null);
  }

  ngOnDestroy(): void {
    this.clearDigitizePoll();
    this.revokeLocalPreview();
  }

  private setLastOcrFromPlan(
    plan: SanctionedPlan,
    meta: Record<string, unknown>
  ): void {
    const dims = plan.dimensionsJson || null;
    const ocrText =
      String(meta['ocrText'] || '') ||
      (dims && typeof dims['ocrText'] === 'string' ? String(dims['ocrText']) : '');
    this.lastOcr.set({
      planId: plan.id,
      plotRef: plan.plotRef,
      engine: String(meta['engine'] || 'mvp-digitize-worker'),
      ocrConfidence: plan.ocrConfidence,
      note: String(meta['note'] || ''),
      sourceBytes: (meta['sourceBytes'] as number | null) ?? null,
      dimensions: dims,
      ocrText,
      cleanedUri: plan.cleanedStorageUri,
      originalUri: plan.originalStorageUri ?? null,
      planVerification:
        (meta['planVerification'] as PlanVerification) ||
        this.readPlanVerification(dims),
    });
    this.previewMode.set('auto');
  }

  readPlanVerification(dims: Record<string, unknown> | null | undefined): PlanVerification | null {
    const pv = dims?.['planVerification'];
    if (!pv || typeof pv !== 'object') return null;
    return pv as PlanVerification;
  }

  verificationClass(ocr: { planVerification?: PlanVerification | null }): string {
    const v = ocr.planVerification?.verdict || ocr.planVerification?.status || '';
    if (v.includes('verified')) return 'verify-ok';
    if (v.includes('mismatch')) return 'verify-bad';
    if (v.includes('incomplete') || v.includes('skipped')) return 'verify-warn';
    return 'verify-neutral';
  }

  verificationHeadline(ocr: { planVerification?: PlanVerification | null }): string {
    const v = ocr.planVerification?.verdict || ocr.planVerification?.status;
    if (!v) {
      return 'Upload a LiDAR image or scan below — the system will auto-compare it against plan OCR data.';
    }
    if (v.includes('verified')) {
      return 'Building matches the approved design (dimensions and footprint align).';
    }
    if (v.includes('mismatch')) {
      return 'Building deviates from the approved design — see details below.';
    }
    if (v.includes('incomplete')) {
      return 'Comparison incomplete — install shapely on the backend or upload a LiDAR scan with measurable footprint.';
    }
    return v;
  }

  onLidarPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.lidarFile = input.files?.[0] || null;
  }

  compareLidarUpload(): void {
    const ocr = this.lastOcr();
    if (!ocr?.planId) {
      this.showToast('Upload and extract the plan PDF first');
      return;
    }
    if (!this.lidarFile) {
      this.showToast('Choose a LiDAR image or .LAS file');
      return;
    }
    this.comparingLidar.set(true);
    this.api.compareLidarToPlan(ocr.planId, this.lidarFile).subscribe({
      next: (res) => {
        this.comparingLidar.set(false);
        const meta = res.meta || {};
        this.setLastOcrFromPlan(res.data, meta);
        this.wizardStep.set(3);
        const verdict = (meta['planVerification'] as PlanVerification)?.verdict || 'done';
        this.showToast(`LiDAR compare: ${verdict}`);
        this.lidarFile = null;
        this.reload();
      },
      error: (err) => {
        this.comparingLidar.set(false);
        this.showToast(err?.error?.message || 'LiDAR compare failed');
      },
    });
  }

  verify(id: number, decision: 'verify' | 'reject'): void {
    this.api.verifyPlan(id, { decision, comment: `UI ${decision}` }).subscribe({
      next: () => {
        this.showToast(`Plan #${id}: ${decision}`);
        this.wizardStep.set(3);
        this.reload();
      },
      error: (err) => this.showToast(err?.error?.message || 'Verify failed (verifier role)'),
    });
  }

  ocrPct(value?: string | number | null): string {
    if (value == null || value === '') return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `${Math.round(n * 100)}%`;
  }

  private showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 4200);
  }
}
